import { lazy, Suspense } from 'react';
import { Input, Textarea, Select, Switch, Button, Card, Sortable, Skeleton } from '@dreamtree-org/twreact-ui';
import { GripVertical, Plus, Trash2 } from 'lucide-react';
import { NamedIcon } from '@dreamtree-org/landing-kit';
import { ICON_NAMES } from '@dreamtree-org/landing-kit/schema';
import ImageField from './ImageField';
import { errorAt } from '../../lib/appearance';

// The editor pulls in tiptap: load it only when a text section is edited.
const RichTextEditor = lazy(() => import('@dreamtree-org/twreact-ui/rich-text').then((m) => ({ default: m.RichTextEditor })));

const ICON_OPTIONS = ICON_NAMES.map((n) => ({ value: n, label: n.replace(/-/g, ' ') }));

/** A blank list item from the field's item definitions. */
function blankItem(field) {
  return Object.fromEntries(field.of.map((f) => [f.key, f.kind === 'list' ? [] : f.default ?? (f.kind === 'text' || f.kind === 'textarea' ? '' : null)]));
}

/**
 * One field of a section, by kind (from landing-kit's SECTION_TYPES). `path` is
 * the field's path inside the section's props ("slides[0].heading") for errors.
 */
export function Field({ field: f, value, onChange, path, errors, draftId }) {
  const error = errorAt(errors, path);
  const common = { label: f.required ? `${f.label} *` : f.label, error };
  switch (f.kind) {
    case 'text':
      return <Input {...common} value={value ?? ''} maxLength={f.max} showCount={!!f.max && f.max <= 160} onChange={(e) => onChange(e.target.value)} />;
    case 'textarea':
      return <Textarea {...common} value={value ?? ''} maxLength={f.max} showCount rows={3} autoResize onChange={(e) => onChange(e.target.value)} />;
    case 'url':
      return <Input {...common} value={value ?? ''} placeholder="/shop or https://…" helperText="A page on your store (/shop), a full https:// address, tel: or mailto:" onChange={(e) => onChange(e.target.value)} />;
    case 'select':
      return <Select {...common} options={f.options} value={value ?? f.default} onChange={(v) => onChange(v)} closeOnSelect />;
    case 'bool':
      return <Switch checked={!!value} onChange={(v) => onChange(v)} rightLabel={f.label} ariaLabel={f.label} />;
    case 'icon':
      return (
        <div className="flex items-end gap-2">
          <span className="mb-2 flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary-50 text-primary-700"><NamedIcon name={value} size={20} /></span>
          <div className="flex-1"><Select {...common} options={ICON_OPTIONS} value={value} onChange={(v) => onChange(v)} closeOnSelect searchable /></div>
        </div>
      );
    case 'image':
      return <ImageField label={common.label} value={value} onChange={onChange} draftId={draftId} error={error} />;
    case 'richtext':
      return (
        <Suspense fallback={<Skeleton height={160} />}>
          <RichTextEditor label={f.label} value={value || ''} onChange={(html) => onChange(html)} outputFormat="html" error={error}
            toolbar={['bold', 'italic', 'heading2', 'heading3', 'bulletList', 'orderedList', 'blockquote', 'link', 'undo', 'redo']} minHeight={160} />
        </Suspense>
      );
    case 'list':
      return <ListField field={f} value={value || []} onChange={onChange} path={path} errors={errors} draftId={draftId} />;
    default:
      return null;
  }
}

/** Fields of an object (a section's props, or one list item). */
export function Fields({ fields, value, onChange, path = '', errors, draftId }) {
  return (
    <div className="space-y-4">
      {fields.map((f) => (
        <Field
          key={f.key}
          field={f}
          value={value ? value[f.key] : undefined}
          onChange={(v) => onChange({ ...(value || {}), [f.key]: v })}
          path={path ? `${path}.${f.key}` : f.key}
          errors={errors}
          draftId={draftId}
        />
      ))}
    </div>
  );
}

/** A reorderable list (slides, FAQ, quotes…) within its min/max. Keys are positions, so inputs keep focus. */
function ListField({ field, value, onChange, path, errors, draftId }) {
  const listError = errorAt(errors, path);
  const label = (item, i) => String((item && item[field.itemLabel]) || '').trim() || `${field.label.replace(/s$/, '')} ${i + 1}`;
  const setAt = (i, next) => onChange(value.map((x, k) => (k === i ? next : x)));
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">{field.label}</span>
        <span className="text-xs opacity-60">{value.length} of {field.max}</span>
      </div>
      <Sortable
        items={value}
        getKey={(item, i) => i}
        getItemLabel={label}
        handle
        onReorder={(next) => onChange(next)}
        renderItem={(item, { index: i, handleProps }) => (
          <Card className="space-y-3 p-3">
            <div className="flex items-center gap-2">
              <span {...handleProps} className={`${handleProps.className || ''} cursor-grab rounded p-1 opacity-60 hover:opacity-100`} aria-label={`Move ${label(item, i)}`} role="button" tabIndex={0}>
                <GripVertical className="h-4 w-4" aria-hidden="true" />
              </span>
              <span className="flex-1 truncate text-sm font-semibold">{label(item, i)}</span>
              <Button size="xs" variant="ghost" aria-label={`Remove ${label(item, i)}`} disabled={value.length <= (field.min || 0)}
                onClick={() => onChange(value.filter((_, k) => k !== i))}>
                <Trash2 className="h-4 w-4" aria-hidden="true" />
              </Button>
            </div>
            <Fields fields={field.of} value={item} path={`${path}[${i}]`} errors={errors} draftId={draftId} onChange={(next) => setAt(i, next)} />
          </Card>
        )}
      />
      {listError ? <p className="text-sm text-error-600">{listError}</p> : null}
      <Button size="sm" variant="outline" leftIcon={<Plus className="h-4 w-4" />} disabled={value.length >= field.max}
        onClick={() => onChange([...value, { ...blankItem(field), ...((field.default && field.default[0]) || {}) }])}>
        Add {field.label.replace(/s$/, '').toLowerCase()}
      </Button>
    </div>
  );
}
